/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorType } from '@ckeditor/ckeditor5-metadata';

import {
	EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER,
	EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER,
	EDITOR_SOURCE_IDENTIFIER,
	EDITOR_WORD_COUNT
} from '../../constants/identifiers';
import type { InstallMethod, Technology } from '../../constants/installation';
import { groupByInstallPackage } from '../groupByInstallPackage/groupByInstallPackage';
import { getTranslationImports } from '../translations/translations';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import { getStylesImport } from '../getStylesImport/getStylesImport';
import { getPluginsImport } from '../getPluginsImport/getPluginsImport';
import { getUsersIntegrationPlugin } from '../getUsersIntegrationPlugin/getUsersIntegrationPlugin';
import { getCommentsIntegrationPlugin } from '../getCommentsIntegrationPlugin/getCommentsIntegrationPlugin';
import { getTrackChangesIntegrationPlugin } from '../getTrackChangesIntegrationPlugin/getTrackChangesIntegrationPlugin';
import { getRevisionHistoryIntegrationPlugin } from '../getRevisionHistoryIntegrationPlugin/getRevisionHistoryIntegrationPlugin';
import { getConfigUpdateAlert } from '../getConfigUpdateAlert/getConfigUpdateAlert';
import { getTokenVariables } from '../getTokenVariables/getTokenVariables';
import type { CustomerPortalAccount } from '../../contexts/CustomerPortalContext';
import { getFilename } from '../getFilename/getFilename';
import type { EditorOptions } from '../../components/SnippetsContainer/Snippets/types';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { requiresWordCountPlugin } from '../requiresWordCountPlugin/requiresWordCountPlugin';
import { getBuilderUrlComment } from '../getBuilderUrlComment/getBuilderUrlComment';
import { getDefaultHexColors } from '../getDefaultHexColors/getDefaultHexColors';

export type GetJsSourceTemplateArgs = {
	customerPortalAccount: CustomerPortalAccount | null;
	editorConfig: string;
	editorOptions: EditorOptions;
	evaluationLicense: string;
	selectedInstallMethod: InstallMethod;
	selectedTechnology: Technology;
	builderUrl: string;
};

export function getJsSourceTemplate( {
	customerPortalAccount,
	editorConfig,
	editorOptions,
	evaluationLicense,
	selectedInstallMethod,
	selectedTechnology,
	builderUrl
}: GetJsSourceTemplateArgs ): string {
	const { editorPlugins, toolbarOptions, editorType, editorLanguage } = editorOptions;

	const cssFileName = getFilename( { type: 'css', selectedTechnology } );
	const groupedPlugins = groupByInstallPackage( editorPlugins );
	const hasPremiumPlugins = groupedPlugins[ 'ckeditor5-premium-features' ].length > 0;
	const configUpdateAlert = getConfigUpdateAlert( { editorPlugins, editorConfig, configRef: 'editorConfig' } );
	const { importsFromCKEditor, importsFromCKEditorPremiumFeatures } = getPluginsImport( editorType, groupedPlugins );
	const pluginsImports = selectedInstallMethod === 'cdn' ?
		`
			const { ${ importsFromCKEditor } } = window.CKEDITOR;
			${ hasPremiumPlugins ? `const { ${ importsFromCKEditorPremiumFeatures } } = window.CKEDITOR_PREMIUM_FEATURES;` : '' }
		` :
		`
			import { ${ importsFromCKEditor } } from 'ckeditor5';
			${ hasPremiumPlugins ? `import { ${ importsFromCKEditorPremiumFeatures } } from 'ckeditor5-premium-features';` : '' }
		`;

	return `
		${ getBuilderUrlComment( builderUrl ) }

		${ pluginsImports }

		${ selectedInstallMethod !== 'cdn' ? getTranslationImports( editorLanguage, hasPremiumPlugins ) : '' }

		${ getStylesImport( selectedInstallMethod, hasPremiumPlugins ) }

		${ selectedInstallMethod === 'npm' ? `import './${ cssFileName }';` : '' }

		${ getTokenVariables( { editorConfig, editorPlugins, customerPortalAccount, evaluationLicense, selectedInstallMethod } ) }

		${ getDefaultHexColors( editorPlugins ) }

		${ getUsersIntegrationPlugin( editorPlugins ) }

		${ getCommentsIntegrationPlugin( editorPlugins ) }

		${ getTrackChangesIntegrationPlugin( editorPlugins ) }

		${ getRevisionHistoryIntegrationPlugin( editorPlugins ) }

		const editorConfig = ${ editorConfig };

		${ configUpdateAlert.call }

		${ getCreatorCallback( editorType, toolbarOptions, editorPlugins ) }

		${ configUpdateAlert.fn }
	`;
}

function getCreatorCallback( editorType: EditorType, toolbarOptions: ToolbarOptions, editorPlugins: EditorPlugins ) {
	const editorSourceElement = `document.querySelector( '${ EDITOR_SOURCE_IDENTIFIER }' )`;
	const editorCreator = `${ editorType }.create( ${ editorSourceElement }, editorConfig )`;
	const hasWordCount = requiresWordCountPlugin( editorPlugins );

	if ( isDecoupledEditor( editorType ) || hasWordCount ) {
		const toolbarCreator = `document.querySelector( '${ EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER }' )
			.appendChild( editor.ui.view.toolbar.element );`;

		const menuBarCreator = `document.querySelector( '${ EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER }' )
			.appendChild( editor.ui.view.menuBarView.element );`;

		const wordCountCreator = `
			const wordCount = editor.plugins.get('WordCount');
			document.querySelector( '${ EDITOR_WORD_COUNT }' ).appendChild( wordCount.wordCountContainer );
		`;

		return `
			${ editorCreator }
				.then( editor => {
					${ hasWordCount ? wordCountCreator : '' }
					${ isDecoupledEditor( editorType ) ? toolbarCreator : '' }
					${ toolbarOptions.isMenuBarUsed ? menuBarCreator : '' }

					return editor;
				} );
		`;
	}

	return `${ editorCreator };`;
}
